import type { AssetId, AssetKind } from '@/shared/types/core'
import type {
  SpatialBindingCreateOptions,
  SpatialBindingFilter,
  SpatialBindingOcclusionDepthSnapshot,
  SpatialBindingSnapshot,
  SpatialOwnerStatePolicy,
  SpatialRepresentationRole,
  SpatialRepresentationState,
  SpatialSurfaceUsage,
  SpatialTransformMode,
} from '@/shared/types/spatial'
import { generateId } from '@/shared/utils'

interface SpatialBindingOwnerState {
  mounted: boolean
  visible: boolean
}

/** 空间绑定记录，描述 owner 资源与 world 空间的关联 */
export interface SpatialBindingRecord {
  /** 绑定唯一 ID */
  id: string
  /** 所属 world 的 ID */
  worldId: string
  /** 源资源 ID */
  ownerAssetId: AssetId
  /** 源资源类型 */
  ownerKind: AssetKind
  /** 参与的空间表达角色列表 */
  representationRoles: SpatialRepresentationRole[]
  /** 表面语义用途列表（仅 surface 角色有效） */
  surfaceUsages: SpatialSurfaceUsage[]
  /** 排序优先级，数值越大越靠前 */
  priority: number
  /** 变换继承模式 */
  transformMode: SpatialTransformMode
  /** owner 状态策略 */
  ownerStatePolicy: SpatialOwnerStatePolicy
  /** owner 当前挂载/可见快照 */
  ownerState: SpatialBindingOwnerState
  /** 共享遮挡深度写入配置 */
  occlusionDepth: SpatialBindingOcclusionDepthSnapshot
}

/**
 * 空间绑定记录仓库，负责 SpatialBindingRecord 的增删查改与快照。
 */
export class SpatialBindingStore {
  private readonly records = new Map<string, SpatialBindingRecord>()

  /**
   * 创建绑定记录。
   *
   * @param options - 创建选项，包含 worldId / owner 信息
   * @param ownerState - owner 当前挂载与可见状态
   * @returns 创建后的绑定记录
   */
  create(
    options: SpatialBindingCreateOptions,
    ownerState: SpatialBindingOwnerState
  ): SpatialBindingRecord {
    const representationRoles = Array.from(new Set(options.representationRoles))
    const surfaceUsages = representationRoles.includes('surface')
      ? Array.from(
          new Set(options.surfaceUsages?.length ? options.surfaceUsages : DEFAULT_SURFACE_USAGES)
        )
      : []

    const record: SpatialBindingRecord = {
      id: options.id ?? generateId(),
      worldId: options.worldId,
      ownerAssetId: options.ownerAssetId,
      ownerKind: options.ownerKind,
      representationRoles,
      surfaceUsages,
      priority: options.priority ?? 0,
      transformMode: options.transformMode ?? 'followOwnerMatrix',
      ownerStatePolicy: options.ownerStatePolicy ?? 'inherit',
      occlusionDepth: normalizeOcclusionDepth(options.occlusionDepth),
      ownerState: {
        mounted: ownerState.mounted,
        visible: ownerState.visible,
      },
    }

    this.records.set(record.id, record)
    return record
  }

  /**
   * 按 ID 获取绑定记录。
   *
   * @param id - 绑定 ID
   * @returns 绑定记录；不存在返回 undefined
   */
  get(id: string): SpatialBindingRecord | undefined {
    return this.records.get(id)
  }

  /**
   * 列出全部绑定记录。
   *
   * @returns 当前绑定记录的数组副本
   */
  list(): SpatialBindingRecord[] {
    return Array.from(this.records.values())
  }

  /**
   * 生成绑定记录的外部快照。
   *
   * @param record - 源绑定记录
   * @param getRepresentationState - 按 worldId/role 查询空间表达状态的回调
   * @returns 快照，含 owner 活跃性与按 role 的可用性
   */
  snapshot(
    record: SpatialBindingRecord,
    getRepresentationState: (
      worldId: string,
      role: SpatialRepresentationRole
    ) => SpatialRepresentationState
  ): SpatialBindingSnapshot {
    const ownerActive = isOwnerActive(record)
    return {
      id: record.id,
      worldId: record.worldId,
      ownerAssetId: record.ownerAssetId,
      ownerKind: record.ownerKind,
      representationRoles: [...record.representationRoles],
      surfaceUsages: [...record.surfaceUsages],
      priority: record.priority,
      transformMode: record.transformMode,
      ownerStatePolicy: record.ownerStatePolicy,
      occlusionDepth: { ...record.occlusionDepth },
      ownerActive,
      availabilityByRole: {
        surface:
          ownerActive &&
          record.representationRoles.includes('surface') &&
          getRepresentationState(record.worldId, 'surface') === 'ready',
        collision:
          ownerActive &&
          record.representationRoles.includes('collision') &&
          getRepresentationState(record.worldId, 'collision') === 'ready',
        navigation:
          ownerActive &&
          record.representationRoles.includes('navigation') &&
          getRepresentationState(record.worldId, 'navigation') === 'ready',
      },
    }
  }

  /**
   * 按过滤条件列出绑定记录。
   *
   * @param filter - 过滤条件，允许为 undefined 表示不过滤
   * @param getRepresentationState - 按 worldId/role 查询空间表达状态的回调
   * @returns 匹配的绑定记录数组；无匹配返回空数组
   */
  filter(
    filter: SpatialBindingFilter | undefined,
    getRepresentationState: (
      worldId: string,
      role: SpatialRepresentationRole
    ) => SpatialRepresentationState
  ): SpatialBindingRecord[] {
    return this.list().filter((record) => {
      if (filter?.ownerAssetId && record.ownerAssetId !== filter.ownerAssetId) {
        return false
      }

      if (filter?.ownerKind && record.ownerKind !== filter.ownerKind) {
        return false
      }

      if (filter?.worldId && record.worldId !== filter.worldId) {
        return false
      }

      if (
        filter?.representationRole &&
        !record.representationRoles.includes(filter.representationRole)
      ) {
        return false
      }

      if (filter?.surfaceUsage) {
        if (
          !record.representationRoles.includes('surface') ||
          !record.surfaceUsages.includes(filter.surfaceUsage)
        ) {
          return false
        }
      }

      if (!filter?.includeInactive) {
        const snapshot = this.snapshot(record, getRepresentationState)
        if (filter?.representationRole) {
          return snapshot.availabilityByRole[filter.representationRole]
        }

        return Object.values(snapshot.availabilityByRole).some(Boolean)
      }

      return true
    })
  }

  /**
   * 更新指定 owner 的状态。
   *
   * @param ownerAssetId - owner 资源 ID
   * @param patch - 挂载/可见字段增量
   * @param ownerKind - 可选限定 owner 类型
   * @returns 受影响的绑定记录数组；无匹配返回空数组
   */
  updateOwnerState(
    ownerAssetId: AssetId,
    patch: Partial<SpatialBindingOwnerState>,
    ownerKind?: AssetKind
  ): SpatialBindingRecord[] {
    const updated: SpatialBindingRecord[] = []
    for (const record of this.records.values()) {
      if (record.ownerAssetId !== ownerAssetId) {
        continue
      }
      if (ownerKind && record.ownerKind !== ownerKind) {
        continue
      }

      record.ownerState = {
        mounted: patch.mounted ?? record.ownerState.mounted,
        visible: patch.visible ?? record.ownerState.visible,
      }
      updated.push(record)
    }

    return updated
  }

  /**
   * 按 ID 移除绑定记录。
   *
   * @param id - 绑定 ID
   * @returns 被移除的记录；不存在返回 undefined
   */
  remove(id: string): SpatialBindingRecord | undefined {
    const record = this.records.get(id)
    if (!record) {
      return undefined
    }

    this.records.delete(id)
    return record
  }

  /**
   * 移除指定 owner 的全部绑定记录。
   *
   * @param ownerAssetId - owner 资源 ID
   * @param ownerKind - 可选限定 owner 类型
   * @returns 被移除的绑定记录数组
   */
  removeByOwner(ownerAssetId: AssetId, ownerKind?: AssetKind): SpatialBindingRecord[] {
    const removed: SpatialBindingRecord[] = []

    for (const record of this.records.values()) {
      if (record.ownerAssetId !== ownerAssetId) {
        continue
      }
      if (ownerKind && record.ownerKind !== ownerKind) {
        continue
      }

      this.records.delete(record.id)
      removed.push(record)
    }

    return removed
  }

  /**
   * 移除指定 worldId 下的全部绑定记录。
   *
   * @param worldId - 目标 world ID
   * @returns 被移除的绑定记录数组
   */
  removeByWorldId(worldId: string): SpatialBindingRecord[] {
    const removed: SpatialBindingRecord[] = []

    for (const record of this.records.values()) {
      if (record.worldId !== worldId) {
        continue
      }

      this.records.delete(record.id)
      removed.push(record)
    }

    return removed
  }
}

function isOwnerActive(record: SpatialBindingRecord): boolean {
  if (record.ownerStatePolicy === 'always') {
    return true
  }
  return record.ownerState.mounted && record.ownerState.visible
}

const DEFAULT_SURFACE_USAGES: SpatialSurfaceUsage[] = ['measurement', 'camera-control']

function normalizeOcclusionDepth(
  value: SpatialBindingCreateOptions['occlusionDepth']
): SpatialBindingOcclusionDepthSnapshot {
  if (value === true) {
    return {
      enabled: true,
      representationRole: 'surface',
    }
  }

  if (value && typeof value === 'object') {
    return {
      enabled: true,
      representationRole: value.representationRole,
    }
  }

  return {
    enabled: false,
    representationRole: null,
  }
}
